// A practitioner schedule is a wall-clock RULE, not an instant: "HH:mm" read in the clinic zone (86d42y068 S7).
import { dayjs } from "../time";
import type { Dayjs } from "dayjs";

// antd pickers want a Dayjs, so a date has to be invented; it is discarded again on write.
const PICKER_DATE = "2024-01-01";

const WALL_CLOCK = /^(\d{1,2}):([0-5]\d)$/;

// A trailing Z or ±HH:MM means the value names an instant rather than a wall clock.
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}[ T](\d{1,2}):([0-5]\d)/;

function pad(hours: number, minutes: number): string | null {
	if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
	if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

// Any stored block value -> "HH:mm"; null when it carries no readable time.
// A zone-less string keeps its time part verbatim: converting it would bake in the pre-S7 bug that
// stamped a browser-clock date onto what the practitioner actually typed.
export function toWallClock(value: unknown): string | null {
	if (value === null || value === undefined || value === "") return null;

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : pad(value.getHours(), value.getMinutes());
	}

	if (typeof value !== "string") return null;

	const trimmed = value.trim();

	const wall = WALL_CLOCK.exec(trimmed);
	if (wall) return pad(Number(wall[1]), Number(wall[2]));

	if (!HAS_OFFSET.test(trimmed)) {
		const naive = NAIVE_DATE_TIME.exec(trimmed);
		if (naive) return pad(Number(naive[1]), Number(naive[2]));
	}

	// An offset-bearing legacy row names an instant with no record of the author's zone, so the
	// viewer's wall clock is the only reading available — which is what every reader did pre-S7.
	const instant = dayjs(trimmed);
	return instant.isValid() ? instant.format("HH:mm") : null;
}

export function wallClockParts(value: unknown): { hours: number; minutes: number } | null {
	const wall = toWallClock(value);
	if (!wall) return null;

	return { hours: Number(wall.slice(0, 2)), minutes: Number(wall.slice(3, 5)) };
}

export function wallClockMinutes(value: unknown): number | null {
	const parts = wallClockParts(value);

	return parts ? parts.hours * 60 + parts.minutes : null;
}

// Minutes from start to end, wrapping past midnight on the same condition healthcare's slot
// generator does (an end at or before the start is an overnight block, not an error).
export function wallClockSpanMinutes(start: unknown, end: unknown): number | null {
	const from = wallClockMinutes(start);
	const to = wallClockMinutes(end);
	if (from === null || to === null) return null;

	return to > from ? to - from : to + 24 * 60 - from;
}

export function isWithinWallClockBlock(start: unknown, end: unknown, minutes: number): boolean {
	const from = wallClockMinutes(start);
	const to = wallClockMinutes(end);
	if (from === null || to === null || from === to) return false;

	return to > from ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

export function toPickerValue(value: unknown): Dayjs | null {
	const wall = toWallClock(value);

	return wall ? dayjs(`${PICKER_DATE} ${wall}`) : null;
}

export function fromPickerValue(value: Dayjs | null | undefined): string | null {
	return value && value.isValid() ? value.format("HH:mm") : null;
}
